import { ConfirmationDialogService } from 'src/app/services/confirmation-dialog.service';
import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { cloneDeep, isEqual } from 'lodash-es';
import { AutoUnsubscribe } from 'ngx-auto-unsubscribe';
import { ActionType, ModelType, UiActions } from '../reference-tree-view/reference.enum';
import { skip, Subject, Subscription } from 'rxjs';
import { ApiConstants } from 'src/app/base/api-constants';
import { ReferenceUtilService } from 'src/app/components/reference-tree-view/reference-util.service';
import { GlobalConstants } from 'src/app/base/global.constants';
import { AppGlobalService } from 'src/app/services/app.global.service';
import { UtilService } from 'src/app/services/util.service';
import { deletePageVariable, getPageVariables, savePageVariable } from 'src/app/STORE/actions/screens.actions';
import {
  deletePageVariableSuccess,
  savePageVariableSuccess,
  setPageVariables
} from 'src/app/STORE/selectors/screens.selector';

import { v4 as uuidv4 } from 'uuid';

@AutoUnsubscribe({ arrayName: 'subscriptions' })
@Component({
  selector: 'vb-page-variables-property',
  templateUrl: './page-variables-property.component.html',
  styleUrls: ['./page-variables-property.component.scss'],
  standalone: false //NOSONAR
})
export class PageVariablesPropertyComponent implements OnInit, OnDestroy {
  private confirmationDialogService = inject(ConfirmationDialogService);
  private appGlobalService = inject(AppGlobalService);
  private util = inject(UtilService);
  private translate = inject(TranslateService);
  private store = inject(Store);
  private referenceUtil = inject(ReferenceUtilService);
  private modelValue: any;
  private onChange: (m: any) => void;
  private onTouched: (m: any) => void;
  @Input() element: any;
  @Input() formData: any;
  @Input() disableField = false;
  @Input() isFromModernize = false;
  @Input() currentScreen: any;
  @Input() currentNode: any = null;
  @Input() currNodeType: any = null;
  @Input() isRDBMS = false;
  isLoading = false;
  pageVariablesProperty: any = [];
  pageVariablesPropertyBck: any = [];
  svgPaths = GlobalConstants.svgPaths;
  allFields: any = [];
  pageVariablesPropertyType = [
    {
      name: 'String',
      value: 'STRING'
    },
    {
      name: 'Number',
      value: 'NUMBER'
    },
    {
      name: 'Date Only',
      value: 'DATE_ONLY'
    },
    {
      name: 'Date Time',
      value: 'DATE_TIME'
    },
    {
      name: 'Double',
      value: 'DOUBLE'
    },
    {
      name: 'Email',
      value: 'EMAIL'
    },
    {
      name: 'Boolean',
      value: 'BOOLEAN'
    }
  ];
  pageVariablesPropertyDefaultValDPV = [
    {
      name: 'Yesterday',
      value: 'yesterday'
    },
    {
      name: 'Today',
      value: 'today'
    },
    {
      name: 'Tomorrow',
      value: 'tomorrow'
    }
  ];
  pageVariablesPropertyDefaultValBoolean = [
    {
      name: this.translate.instant('TRUE'),
      value: true
    },
    {
      name: this.translate.instant('FALSE'),
      value: false
    }
  ];
  backupVal: any;
  subscriptions: Subscription[] = [];
  savePageVariableSubject: any;
  variablesInQueue: any = [];
  variablesInQueueActivated = false;

  get isBetaAccessPrivilegeConsumable(): boolean {
    return this.appGlobalService.betaAccessPrivilegeForApp;
  }

  get model() {
    return this.modelValue;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  writeValue(value: any): void {
    this.modelValue = value;
    if (value) {
      this.backupVal = value;
    }
  }

  ngOnInit(): void {
    this.savePageVariableSubject = new Subject<string>();
    this.pageVariablesActions();
    this.fetchVariables();
    const pageVariables$ = this.savePageVariableSubject.subscribe((value: any) => {
      this.store.dispatch(
        savePageVariable({
          savePageVariableApiConst:
            this.pageVariablesProperty[value.rowIndex].recordSaved || value?.update
              ? ApiConstants.updatePageVariable
              : ApiConstants.savePageVariable,
          data: value.data
        })
      );
    });
    this.addToSubscriptionsList(pageVariables$);
  }

  pageVariablesActions() {
    const pageVariables$ = this.store
      .select(setPageVariables)
      .pipe(skip(1))
      .subscribe((response: any) => {
        let renamedData = response?.map((item) => {
          return {
            ...item,
            ...{
              pageVariablesPropertyName: item.name,
              pageVariablesPropertyType: item.type,
              pageVariablesPropertyDefaultValue: item.defaultValue,
              recordSaved: true
            }
          };
        });
        renamedData = renamedData || [];
        if (this.pageVariablesProperty?.length === 0) {
          this.pageVariablesProperty = renamedData;
        } else {
          this.pageVariablesProperty.forEach((originalObj, index) => {
            Object.keys(originalObj).forEach((key) => {
              if (originalObj[key] !== renamedData[index][key]) {
                originalObj[key] = renamedData[index][key];
              }
            });
          });
        }
        this.pageVariablesPropertyBck = cloneDeep(this.pageVariablesProperty);
      });
    this.addToSubscriptionsList(pageVariables$);
    const deletePageVariableSuccess$ = this.store
      .select(deletePageVariableSuccess)
      .pipe(skip(1))
      .subscribe((response: any) => {
        if (response?.pageVariableSid) {
          this.pageVariablesProperty = this.pageVariablesProperty.filter((obj) => {
            return obj.sid !== response?.pageVariableSid;
          });
          this.pageVariablesPropertyBck = cloneDeep(this.pageVariablesProperty);
        }
      });
    this.addToSubscriptionsList(deletePageVariableSuccess$);
    const savePageVariableSuccess$ = this.store
      .select(savePageVariableSuccess)
      .pipe(skip(1))
      .subscribe((response: any) => {
        if (response?.sid) {
          if (this.variablesInQueue?.length > 0) {
            this.variablesInQueue.forEach((obj: any) => {
              this.savePageVariableSubject.next({ rowIndex: obj.rowIndex, data: obj.data, update: true });
            });
            this.variablesInQueue = [];
          }
          const newKeys = {
            name: 'pageVariablesPropertyName',
            type: 'pageVariablesPropertyType',
            defaultValue: 'pageVariablesPropertyDefaultValue'
          };
          this.pageVariablesProperty.forEach((originalObj, index) => {
            if (originalObj.sid === response?.sid) {
              Object.keys(originalObj).forEach((key) => {
                if (key === newKeys[key] && originalObj[key] !== response[newKeys[key]]) {
                  originalObj[key] = response[newKeys[key]];
                }
              });
              originalObj.recordSaved = true;
            }
          });
          this.pageVariablesPropertyBck = cloneDeep(this.pageVariablesProperty);
        }
        this.variablesInQueueActivated = false;
      });
    this.addToSubscriptionsList(savePageVariableSuccess$);
  }

  fetchVariables() {
    const data = {
      orgId: this.currentScreen?.orgId,
      appId: this.currentScreen?.appId,
      pageSid: this.currentScreen?.sid
    };
    this.store.dispatch(getPageVariables({ getPageVariablesApiConst: ApiConstants.getPageVariables, data: data }));
  }

  addVariable() {
    const len = this.pageVariablesProperty.length;
    if (len > 0 && !this.pageVariablesProperty[len - 1]?.pageVariablesPropertyName?.trim()) {
      this.util.showMessage('error', this.translate.instant('ADD_VARIABLES_PROPERTY_ERR_MSG'));
      return;
    } else if (
      len > this.pageVariablesPropertyBck?.length &&
      this.pageVariablesProperty[len - 1]?.pageVariablesPropertyName?.trim()
    ) {
      return;
    }
    const paramMap = {
      pageVariablesPropertyName: null,
      pageVariablesPropertyType: 'STRING',
      pageVariablesPropertyDefaultValue: null,
      sid: uuidv4()
    };
    this.pageVariablesProperty = [...this.pageVariablesProperty, paramMap];
  }

  deletePageVariable(rowIndex, data?) {
    const confirmDeleteCallback = () => {
      this.deleteVariable(rowIndex, data);
    };
    let ConfirmationMessage;
    if (data.pageVariablesPropertyName != null) {
      ConfirmationMessage = this.translate.instant('PAGE_VARIABLE_DELETE_CONFIRMATION_WITH_VARIABLE_NAME', {
        variableName: data.pageVariablesPropertyName
      });
    } else {
      ConfirmationMessage = this.translate.instant('PAGE_VARIABLE_DELETE_CONFIRMATION');
    }

    this.confirmationDialogService.confirm({
      message: ConfirmationMessage,
      header: 'Confirmation',
      accept: confirmDeleteCallback,
      reject: () => {
        //do nothing on reject
      }
    });
  }

  deleteVariable(rowIndex, _data?) {
    if (this.pageVariablesProperty[rowIndex]?.recordSaved) {
      this.referenceUtil
        .handleWithReferences({
          payload: {
            modelId: this.pageVariablesProperty[rowIndex]?.sid,
            modelType: ModelType.PAGE_VARIABLE,
            actionType: ActionType.PAGE_VARIABLE_DELETE,
            action: UiActions.DELETE
          },
          node: {
            name: this.pageVariablesPropertyBck[rowIndex]?.pageVariablesPropertyName,
            type: ModelType.PAGE_VARIABLE
          },
          onConfirm: () => this.confirmDeletion(rowIndex),
          onNoReferences: () => this.confirmDeletion(rowIndex),
          onCancel: () => {
            this.pageVariablesProperty = cloneDeep(this.pageVariablesPropertyBck);
          },
          onError: () => this.confirmDeletion(rowIndex)
        })
        .catch(() => {
          /* ignored */
        });
    } else {
      this.pageVariablesProperty.splice(rowIndex, 1);
    }
  }

  confirmDeletion(rowIndex) {
    const data = {
      orgId: this.currentScreen?.orgId,
      appId: this.currentScreen?.appId,
      pageVariableSid: this.pageVariablesProperty[rowIndex]?.sid
    };
    this.store.dispatch(
      deletePageVariable({ deletePageVariableApiConst: ApiConstants.deletePageVariable, data: data })
    );
  }

  onPastePVName(event: any, rowIndex?): void {
    const value = event?.target?.value;
    if (event?.target?.value?.length > 50) {
      event.preventDefault();
    } else {
      this.pageVariablesProperty[rowIndex].pageVariablesPropertyName = value;
    }
  }

  validateReservedKeyword(propertyName: any) {
    if (
      Object.keys(
        this.util.validateTableName(propertyName, {}, this.isRDBMS, true, false, 'Page Variable Name')?.errorMessage
      )?.length > 0
    ) {
      return true;
    }
    return false;
  }

  onValueChange(event, property?, rowIndex?) {
    let propertyName =
      this.pageVariablesProperty[rowIndex].pageVariablesPropertyName || (property === 'name' ? event.target.value : '');
    let defaultValue = this.pageVariablesProperty[rowIndex].pageVariablesPropertyDefaultValue;
    if (
      propertyName &&
      (!this.util.isValidAlphaNumericUnderscoreValue(propertyName) || this.validateReservedKeyword(propertyName))
    ) {
      return;
    } else if (
      propertyName &&
      this.util.findDuplicates(this.pageVariablesProperty, 'pageVariablesPropertyName')?.length > 0
    ) {
      this.util.showMessage(
        'error',
        this.translate.instant('DUPLICATE_PAGE_VARIABLE').replace('{field}', propertyName)
      );
      propertyName = null;
      return;
    } else if (property === 'type' && rowIndex > -1) {
      defaultValue = this.pageVariablesProperty[rowIndex].pageVariablesPropertyType === 'BOOLEAN' ? true : null;
      this.pageVariablesProperty[rowIndex].pageVariablesPropertyDefaultValue = defaultValue;
    }

    if (
      propertyName &&
      this.pageVariablesProperty[rowIndex].pageVariablesPropertyType === 'EMAIL' &&
      defaultValue &&
      !this.util.isValidEmail(defaultValue)
    ) {
      this.util.showMessage('error', this.translate.instant('EMAIL_PTERN_MSG'));
    } else if (propertyName && propertyName?.length > 50) {
      this.util.showMessage(
        'error',
        this.translate.instant('PAGE_VARIABLE_NAME_MAX_LEN_ERROR').replace('{maxLength}', 50)
      );
    } else if (propertyName) {
      this.saveVariables(rowIndex, propertyName, defaultValue);
    } else if (!propertyName) {
      this.util.showMessage('error', this.translate.instant('PAGE_VARIABLE_NAME_IS_REQUIRED'));
    }
  }

  saveVariables(rowIndex, propertyName, defaultValue) {
    // Prepare data for saving
    const data = {
      orgId: this.currentScreen?.orgId,
      appId: this.currentScreen?.appId,
      tableSid: this.currentScreen.tableId,
      pageSid: this.currentScreen?.sid,
      name: propertyName,
      type: this.pageVariablesProperty[rowIndex].pageVariablesPropertyType,
      defaultValue: defaultValue,
      sid: this.pageVariablesProperty[rowIndex].sid
    };

    // Check for changes
    // Check whether the name has changed
    const recordNameStatus =
      propertyName?.toLowerCase() !=
        this.pageVariablesPropertyBck[rowIndex]?.pageVariablesPropertyName?.toLowerCase() || false;

    // Check whether the default value has changed
    const recorddefValStatus =
      defaultValue != this.pageVariablesPropertyBck[rowIndex]?.pageVariablesPropertyDefaultValue || false;

    // Check whether the type has changed
    const pageVariablesPropertyType =
      this.pageVariablesProperty[rowIndex]?.pageVariablesPropertyType !=
        this.pageVariablesPropertyBck[rowIndex]?.pageVariablesPropertyType || false;

    // Detect the reference scenarios first because default value will also be "null" on type change
    if (pageVariablesPropertyType) {
      const actionType = ActionType.PAGE_VARIABLE_TYPE_UPDATE;
      const uiAction = UiActions.UPDATE;
      this.referenceUtil
        .handleWithReferences({
          payload: {
            modelId: this.pageVariablesProperty[rowIndex]?.sid,
            modelType: ModelType.PAGE_VARIABLE,
            actionType: actionType,
            action: uiAction
          },
          node: {
            name: this.pageVariablesPropertyBck[rowIndex]?.pageVariablesPropertyName,
            type: ModelType.PAGE_VARIABLE
          },
          onConfirm: () => this.confirmUpdate(rowIndex, data),
          onNoReferences: () => this.confirmUpdate(rowIndex, data),
          onCancel: () => {
            this.pageVariablesProperty = cloneDeep(this.pageVariablesPropertyBck);
          },
          onError: () => {
            this.pageVariablesProperty = cloneDeep(this.pageVariablesPropertyBck);
          }
        })
        .catch(() => {
          /* ignored */
        });
    } else if (!this.pageVariablesProperty[rowIndex].recordSaved || recorddefValStatus || recordNameStatus) {
      this.confirmUpdate(rowIndex, data);
    }
  }

  confirmUpdate(rowIndex, data) {
    const pSidFlag = this.pageVariablesPropertyBck.some(
      (value) => value.sid === this.pageVariablesProperty[rowIndex].sid
    );
    if (pSidFlag || !this.variablesInQueueActivated) {
      if (!pSidFlag) {
        this.variablesInQueueActivated = true;
      }
      this.savePageVariableSubject.next({ rowIndex, data });
    } else if (!this.variablesInQueue.some((obj) => isEqual(obj, { rowIndex, data }))) {
      this.variablesInQueue.push({ rowIndex, data });
    }
  }

  addToSubscriptionsList(subName) {
    this.subscriptions.push(subName);
  }

  showWhereUsed(rowIndex: any, data: any): void {
    this.isLoading = true;
    this.referenceUtil.showWhereUsed(
      {
        id: data.sid,
        name: data.pageVariablesPropertyName,
        type: this.translate.instant('PAGE_VARIABLE')
      },
      { hideLoader: () => (this.isLoading = false) }
    );
  }

  onNameChange(value: string, rowIndex: number): void {
    this.onValueChange({ target: { value } }, 'name', rowIndex);
  }

  onDefaultValueChange(value: any, rowIndex: number): void {
    this.onValueChange({ target: { value } }, '', rowIndex);
  }

  onInputNumberEnterKey(event: KeyboardEvent, data: any, rowIndex: number): void {
    if (event.key === 'Enter') {
      this.onDefaultValueChange(data.pageVariablesPropertyDefaultValue, rowIndex);
    }
  }
  
  ngOnDestroy(): void {
    console.log('on destroy triggered for page-variables');
  }
}


